<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import IconFacebook from '@/components/ui/icons/IconFacebook.vue'
import IconLinkedIn from '@/components/ui/icons/IconLinkedIn.vue'
import IconXSocial from '@/components/ui/icons/IconXSocial.vue'

const { t, locale } = useI18n()

const currentYear = computed(() => new Date().getFullYear())

function navPath(path: string) {
  return `/${locale.value}${path}`
}

const productLinks = [
  { label: 'Personalizer', path: '/products/personalizer' },
  { label: 'Maira', path: '/products/maira' },
  { label: 'G-Core', path: '/products/gcore' },
  { label: 'SmartAds', path: '/products/smartads' },
]

const companyLinks = computed(() => [
  { label: t('footer.aboutUs'), path: '/company/about-us' },
  { label: t('nav.corporateProfile'), path: '/company/corporate-profile' },
  { label: t('nav.ourTeam'), path: '/company/team' },
  { label: t('nav.career'), path: '/company/career' },
])

const quickLinks = computed(() => [
  { label: t('nav.events'), href: 'https://event.gigalogy.com/' },
  { label: t('footer.sandbox'), href: 'https://api.recommender.gigalogy.com/v1/sandbox#/' },
  { label: t('footer.documentation'), href: 'https://api.recommender.gigalogy.com/v1/documentation' },
  { label: t('footer.tutorial'), href: 'https://tutorial.gigalogy.com/' },
])
</script>

<template>
  <footer class="bg-surface-dark overflow-hidden relative">
    <!-- Radial glow -->
    <div
      class="absolute left-1/2 -translate-x-1/2 -top-80 size-[600px] rounded-full pointer-events-none"
      style="background: radial-gradient(circle, rgba(38,190,200,0.15) 0%, transparent 70%)"
      aria-hidden="true"
    />

    <div class="page-container relative pt-20">

      <!-- Top section -->
      <div class="flex flex-col lg:flex-row items-start justify-between gap-12">

        <!-- Brand column -->
        <div class="flex flex-col gap-9 w-full lg:w-[400px] shrink-0">
          <div class="flex flex-col gap-6">
            <RouterLink :to="navPath('')">
              <img src="/images/gigalogy-logo.png" alt="Gigalogy" class="h-10 w-auto" />
            </RouterLink>
            <p class="text-input-text text-body-md">
              {{ t('footer.tagline') }}
            </p>
          </div>

          <!-- Social icons -->
          <div class="flex items-center gap-7">
            <a href="https://www.facebook.com/gigalogy" target="_blank" rel="noopener noreferrer" aria-label="Facebook" class="text-input-text hover:text-white transition-colors">
              <IconFacebook />
            </a>
            <a href="https://www.linkedin.com/company/gigalogy" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" class="text-input-text hover:text-white transition-colors">
              <IconLinkedIn />
            </a>
            <a href="https://twitter.com/gigalogy" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" class="text-input-text hover:text-white transition-colors">
              <IconXSocial />
            </a>
          </div>
        </div>

        <!-- Nav columns: grid on mobile; dissolve into the flex row on lg so
             justify-between distributes the gaps evenly like the Figma design -->
        <div class="grid grid-cols-2 md:grid-cols-3 gap-10 w-full lg:contents">

          <!-- Products -->
          <div class="flex flex-col gap-6 lg:w-[200px] shrink-0">
            <p class="font-medium text-white text-nav">{{ t('nav.products') }}</p>
            <ul class="flex flex-col gap-5">
              <li v-for="item in productLinks" :key="item.path">
                <RouterLink :to="navPath(item.path)" class="text-input-text text-body-md hover:text-white transition-colors">
                  {{ item.label }}
                </RouterLink>
              </li>
            </ul>
          </div>

          <!-- Company -->
          <div class="flex flex-col gap-6 lg:w-[200px] shrink-0">
            <p class="font-medium text-white text-nav">{{ t('nav.company') }}</p>
            <ul class="flex flex-col gap-5">
              <li v-for="item in companyLinks" :key="item.path">
                <RouterLink :to="navPath(item.path)" class="text-input-text text-body-md hover:text-white transition-colors">
                  {{ item.label }}
                </RouterLink>
              </li>
            </ul>
          </div>

          <!-- Quick Links -->
          <div class="flex flex-col gap-6 lg:w-[200px] shrink-0">
            <p class="font-medium text-white text-nav">{{ t('footer.quickLinks') }}</p>
            <ul class="flex flex-col gap-5">
              <li v-for="item in quickLinks" :key="item.href">
                <a :href="item.href" target="_blank" rel="noopener noreferrer" class="text-input-text text-body-md hover:text-white transition-colors">
                  {{ item.label }}
                </a>
              </li>
            </ul>
          </div>

        </div>
      </div>

      <!-- Bottom bar -->
      <div class="mt-20 border-t border-black-light/30 flex flex-col md:flex-row items-center justify-between gap-4 py-6">
        <div class="flex flex-wrap items-center gap-3">
          <RouterLink :to="navPath('/privacy-policy')" class="text-input-text text-body-md hover:text-white transition-colors">{{ t('footer.privacyPolicy') }}</RouterLink>
          <span class="text-black-light">|</span>
          <RouterLink :to="navPath('/terms-and-conditions')" class="text-input-text text-body-md hover:text-white transition-colors">{{ t('footer.termsConditions') }}</RouterLink>
          <span class="text-black-light">|</span>
          <RouterLink :to="navPath('/cookie-policy')" class="text-input-text text-body-md hover:text-white transition-colors">{{ t('footer.cookiePolicy') }}</RouterLink>
          <span class="text-black-light">|</span>
          <RouterLink :to="navPath('/commercial-disclosure')" class="text-input-text text-body-md hover:text-white transition-colors">{{ t('footer.commercialDisclosure') }}</RouterLink>
        </div>
        <p class="text-input-text text-body-md">{{ t('footer.copyright', { year: currentYear }) }}</p>
      </div>

    </div>
  </footer>
</template>
